import Head from "next/head";
import { Link } from "@mui/material";
import { TbShirtSport } from "react-icons/tb";
import { HiOutlineTicket } from "react-icons/hi";
import { MdOutlineEventSeat, MdOutlinedFlag } from "react-icons/md";

import { constants } from "../../constants";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

const AviatorsLink = () => (
  <Link
    target="_blank"
    sx={{
      fontWeight: 500,
      color: "primary.main",
      textDecoration: "none",
    }}
    href="https://am.ticketmaster.com/aviatorslv/"
  >
    Account Manager
  </Link>
);

export const purchaseSeatsLanguages = {
  similarSeatModalPrimaryButton: "Apply selection for all games",
  similarSeatModalSecondaryButton: "I want to choose seats",
  similarSeatModalBody: (seatTitle: string) => {
    return (
      <>
        Do you want to apply <b>{seatTitle.replace(/<br \/>/g, "")}</b> for all
        games in your pack?
      </>
    );
  },
  progressBarAditionalText:
    "to complete your purchase for the Pick 5 Flex Plan!",
  progressBarSuccessText:
    "Congrats! You've selected all your games for the Pick 5 Flex Plan - checkout now.",
};

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Choose Your Games",
    description: "Select the games you wish to attend.",
  },
  {
    icon: <MdOutlineEventSeat />,
    title: "Login to Account Manager",
    description: (
      <>
        Click continue and connect your Aviators <AviatorsLink /> using our
        secure login.
      </>
    ),
  },
  {
    icon: <TbShirtSport />,
    title: "Select Your Games",
    description: "Select the seats you wish to purchase for each game.",
  },

  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description: (
      <>
        Tickets will be available in your <AviatorsLink /> within 24 hours of
        purchase. Reach out to Live Fan Support for any questions.
      </>
    ),
  },
];

export const faqs = [
  {
    title: "How does the Aviators Pick 5 Flex Plan work?",
    description: (
      <>
        The Aviators Pick 5 Flex Plan allows fans to select any seat to any
        game. Simply log into your Las Vegas Ballpark <AviatorsLink /> and
        select games.
      </>
    ),
  },
  {
    title: "Do I have to select all my games now?",
    description:
      "Yes, you have to choose a minimum of 5 games that you'd like to attend.",
  },
  {
    title: "Can I choose seats throughout the venue?",
    description: "Yes, you can choose from all current available inventory.",
  },
  {
    title: "Where are event tickets delivered?",
    description: (
      <>
        Tickets will be available in the Las Vegas Ballpark <AviatorsLink />.
      </>
    ),
  },
];

export function CheckoutTerms() {
  return (
    <>
      By purchasing you agree to receive SMS alerts regarding ticket delivery
      and agree to the{" "}
      <Link
        href={
          "https://uploads-ssl.webflow.com/5ed7df6fe67e862e0a62f9f2/616882d2b691e09f75348396_Terms%20%26%20Conditions.pdf"
        }
        target={"_blank"}
        sx={{
          color: "primary.main",
          textDecoration: "none",
          fontWeight: 500,
        }}
      >
        Terms & Conditions
      </Link>{" "}
      and{" "}
      <Link
        href={
          "https://uploads-ssl.webflow.com/5ed7df6fe67e862e0a62f9f2/63c05d80816dac05b5b34ac7_seasonshare%20CCPA%20Privacy%20Policy%20Agreement.pdf"
        }
        target={"_blank"}
        sx={{
          color: "primary.main",
          textDecoration: "none",
          fontWeight: 500,
        }}
      >
        Privacy Policy
      </Link>
      .
    </>
  );
}

export const AnalyticsHeader = (props: { teamConfig: TeamConfig }) => {
  const { teamSchema } = props.teamConfig;

  return (
    <>
      <Head>
        <script
          nonce={process.env.pixelScriptNonce}
          id={`${teamSchema}-meta-pixel-script`}
          defer
          dangerouslySetInnerHTML={{
            __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '3360222884215614');
              fbq('track', 'PageView');
              console.log('meta pixel page load event fired.');
              window.addEventListener("${constants.customEvents.eventPurchaseSuccess}", function(e) {
                try {
                  const { cartRes, checkoutRes } = e.detail;
                  fbq('track', 'Purchase', {
                    value: String(cartRes.allInPrice), 
                    currency: 'USD', 
                  });
                  console.log('meta pixel purchase event fired.');
                } catch (error) {
                  console.log('Error in meta pixel purchase event', error); 
                }
              });
            `,
          }}
        />

        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: "none" }}
            src="https://www.facebook.com/tr?id=3360222884215614&ev=PageView&noscript=1"
          />
        </noscript>
      </Head>
    </>
  );
};
